import React, {useEffect, useState} from "react";
import {Box, Divider as MuiDivider, Theme, Typography, useMediaQuery} from "@material-ui/core";
import InfoBlock from "./components/InfoBlock";
import styled from "styled-components/macro";
import {spacing} from "@material-ui/system";
import {Helmet} from "react-helmet-async";
import SearchBlock from "../../../components/SearchBlock";
import CustomBreadcrumbs from "../../../components/CustomBreadcrumbs";
import {useSelector} from "../../../redux/store";
import {clearProperties, fetchPagedProperties} from "../../../redux/actions/propertyActions";
import {useDispatch} from "react-redux";
import PropertiesWrapper from "./components/PropertiesWrapper";
import Select from "./components/Select";
import {Grid} from "@mui/material";
import {Pagination} from "@material-ui/lab";
import {fetchPropertyCategories} from "../../../services/propertyFormService";
import {fetchA11yStandard} from "../../../redux/actions/a11yStandardActions";
import {A11yStandardType} from "../../../models/utils";
import NoHaveData from "./components/NoHaveData";
import GridSkeleton from "../../../components/GridSkeleton";
import {useTranslation} from "react-i18next";
import {IUser} from "../../../models/User";
import {getCompanies, getUserProfile} from "../../../selectors";
import {clearUnits} from "../../../redux/actions/unitsActions";
import {clearProperty} from "../../../redux/actions-declarations/property";
import SelectCompany from "../standards/components/SelectCompany";
import {fetchCompaniesInLive} from "../../../redux/actions/companyActions";

const Divider = styled(MuiDivider)(spacing);

const getPropertyTypes = (a11yStandard: A11yStandardType[]) => {
  if (a11yStandard.length > 0) {
    return a11yStandard.map((item) => ({title: item.title, key: item.key}))
  }
  return []
}

const Widget = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.authReducer.user!)
  const properties = useSelector((state) => state.propertyReducer.properties)
  const a11yStandard = useSelector((state) => state.a11yStandardReducer.a11yStandard)
  const count = useSelector((state) => state.propertyReducer.count)
  const loadingProperties = useSelector((state) => state.propertyReducer.loading.properties)
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const userProfile = useSelector<IUser | undefined>(getUserProfile);
  const companies = useSelector<any>(getCompanies)

  const [searchText, setSearchText] = useState<string>('')
  const [selectedA11yStandard, setSelectedA11yStandard] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [categoryList, setCategoryList] = useState<any>([])
  const [selectedCompany, setSelectedCompany] = useState<string>('')

  useEffect(() => {
    if (userProfile?.is_staff) {
      dispatch(fetchCompaniesInLive(user!.token, false))
    }
  }, [userProfile]);

  const getProperties = (
    _page: number = page,
    _selectedA11yStandard = selectedA11yStandard,
    _selectedCategory = selectedCategory,
  ) => {
    dispatch(fetchPagedProperties(
      user.token,
      _page,
      searchText,
      {
        a11yStandard: _selectedA11yStandard,
        category: _selectedCategory,
        company: selectedCompany,
      },
      false
    ));
  }

  useEffect(() => {
    dispatch(clearUnits())
    dispatch(clearProperty())
    if (properties.length === 0) {
      getProperties()
    }
    if (a11yStandard.length === 0) {
      dispatch(fetchA11yStandard(user!.token))
    }
    return () => {
      dispatch(clearProperties())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedA11yStandard) {
      setSelectedCategory('')
      getCategories(selectedA11yStandard)
      getProperties(1, selectedA11yStandard, '')
    }
  }, [selectedA11yStandard])

  useEffect(() => {
    if (selectedCategory) {
      getProperties(1)
    }
  }, [selectedCategory])

  useEffect(() => {
    getProperties(1)
  }, [selectedCompany]);

  const getCategories = async (propertyType: string) => {
    const categories = await fetchPropertyCategories(propertyType, user!.token);
    if (categories.results.length > 0) {
      setCategoryList(categories.results)
    }
  }

  const handleChangePage = (_: any, value: number) => {
    getProperties(value)
    setPage(value);
  };

  return (
    <Box p={12} className={`disable-scrolls`} overflow="scroll">
      <Helmet title={t('widget')} />
      <Typography variant="h3" gutterBottom display="inline">
        {t('widget')}
      </Typography>
      <CustomBreadcrumbs
        current={t('widget')}
        before={[{to: '/', title: t('dashboard')}]}
      />
      <Divider my={6} />
      <InfoBlock />
      <Box mb={4}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <SearchBlock
              value={searchText}
              setValue={setSearchText}
              onClick={() => getProperties(1)}
            />
          </Grid>
          {userProfile?.is_staff ? (
            <>
              <Grid item xs={12} sm={3} md={3} lg={2}>
                <Select
                  placeholder={t('property_type')}
                  selected={selectedA11yStandard}
                  setSelected={setSelectedA11yStandard}
                  list={getPropertyTypes(a11yStandard)}
                  onClear={() => {
                    getProperties(1, '', '')
                    setSelectedCategory('')
                    setCategoryList([])
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={3} lg={2}>
                <Select
                  placeholder={t('category')}
                  selected={selectedCategory}
                  setSelected={setSelectedCategory}
                  list={categoryList}
                  onClear={() => getProperties(1, selectedA11yStandard, '')}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={3} lg={2} display={'flex'}>
                <SelectCompany
                  selected={selectedCompany}
                  setSelected={setSelectedCompany}
                  list={companies ? companies : []}
                />
              </Grid>
            </>
          ) : null}
        </Grid>
      </Box>
      {loadingProperties ? (
        <Box mt={3}>
          <GridSkeleton
            count={8}
            height={254}
          />
        </Box>
      ) : (
        <PropertiesWrapper properties={properties}/>
      )}

      <Box mt={6}>
        <NoHaveData
          show={!loadingProperties && properties.length === 0}
          content={t('no_widget_data_message')}
        />
      </Box>

      {count && properties.length > 0 ? (
        <Box mt={6}>
          <Pagination
            size={sm ? "small" : "medium"}
            count={Math.ceil(count / 8)}
            page={page}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
          />
        </Box>
      ) : null}
    </Box>
  )
}

export default Widget